import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Mail, ExternalLink, FileText } from 'lucide-react';

const Talks: React.FC = () => {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-6xl mx-auto"
    >
      {/* Header Section */}
      <div className="text-center mb-12">
        <motion.h1 
          className="font-display text-5xl md:text-7xl font-semibold text-stone-800 mb-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {t('talks.title')}
        </motion.h1>
        <motion.p 
          className="text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {t('talks.subtitle')}
        </motion.p>
      </div>

      {/* 2025 Talks */}
      <motion.div 
        className="mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        <h2 className="font-display text-3xl font-semibold text-stone-800 mb-6 flex items-center">
          <span className="w-8 h-px bg-stone-500 mr-4"></span>
          2025
        </h2>
        <div className="space-y-6">
          <motion.div 
            className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-stone-200/70 hover:shadow-xl transition-all duration-300 group"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-3">
                  <span className="text-sm font-semibold text-[#5b6678] bg-[#eef1f5] px-3 py-1 rounded-full">
                    06/11 - 16:00 UTC+1
                  </span>
                </div>
                <h3 className="font-display text-3xl font-semibold text-stone-800 mb-2 group-hover:text-[#5b6678] transition-colors duration-300">
                  {t('talks.talks2025.fineTuningImperial.title')}
                </h3>
                <p className="text-stone-700 mb-3">
                  <span className="font-medium">{t('talks.venue')}:</span> {t('talks.talks2025.fineTuningImperial.venue')}
                </p>
                <p className="text-stone-600 text-sm">
                  {t('talks.talks2025.fineTuningImperial.description')}
                </p>
              </div>
              <div className="ml-4">
                <img 
                  src="/files/talks/imgs/imperial.png" 
                  alt="Imperial College London" 
                  className="w-16 h-16 object-contain rounded-lg shadow-sm"
                />
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-stone-200/70 hover:shadow-xl transition-all duration-300 group"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.0 }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-3">
                  <span className="text-sm font-semibold text-[#5b6678] bg-[#eef1f5] px-3 py-1 rounded-full">
                    20/05 - 17:00 UTC+1
                  </span>
                </div>
                <h3 className="font-display text-3xl font-semibold text-stone-800 mb-2 group-hover:text-[#5b6678] transition-colors duration-300">
                  {t('talks.talks2025.llmMarkov.title')}
                </h3>
                <p className="text-stone-700 mb-3">
                  <span className="font-medium">{t('talks.venue')}:</span> {t('talks.talks2025.llmMarkov.venue')}
                </p>
                <p className="text-stone-600 text-sm">
                  {t('talks.talks2025.llmMarkov.description')}
                </p>
              </div>
              <div className="ml-4">
                <img 
                  src="/files/talks/imgs/MPI.png" 
                  alt="Max Planck Institute for Software Systems" 
                  className="w-16 h-16 object-contain rounded-lg shadow-sm"
                />
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-stone-200/70 hover:shadow-xl transition-all duration-300 group"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.2 }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-3">
                  <span className="text-sm font-semibold text-[#5b6678] bg-[#eef1f5] px-3 py-1 rounded-full">
                    26/03 - 15:00 UTC+1
                  </span>
                </div>
                <h3 className="font-display text-3xl font-semibold text-stone-800 mb-2 group-hover:text-[#5b6678] transition-colors duration-300">
                  {t('talks.talks2025.fineTuning.title')}
                </h3>
                <p className="text-stone-700 mb-3">
                  <span className="font-medium">{t('talks.venue')}:</span> {t('talks.talks2025.fineTuning.venue')}
                </p>
                <p className="text-stone-600 text-sm">
                  {t('talks.talks2025.fineTuning.description')}
                </p>
              </div>
              <div className="ml-4">
                <img 
                  src="/files/talks/imgs/deepmind.png" 
                  alt="Google DeepMind" 
                  className="w-16 h-16 object-contain rounded-lg shadow-sm"
                />
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-stone-200/70 hover:shadow-xl transition-all duration-300 group"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.4 }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-3">
                  <span className="text-sm font-semibold text-[#5b6678] bg-[#eef1f5] px-3 py-1 rounded-full">
                    23/01 - 14:00 UTC+1
                  </span>
                </div>
                <h3 className="font-display text-3xl font-semibold text-stone-800 mb-2 group-hover:text-[#5b6678] transition-colors duration-300">
                  {t('talks.talks2025.llmMarkovImperial.title')}
                </h3>
                <p className="text-stone-700 mb-3">
                  <span className="font-medium">{t('talks.venue')}:</span> {t('talks.talks2025.llmMarkovImperial.venue')}
                </p>
                <p className="text-stone-600 text-sm mb-3">
                  {t('talks.talks2025.llmMarkovImperial.description')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="/assets/LLM_as_Markov_Chains_LC2.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-2 py-1 bg-[#eef1f5] text-[#5b6678] text-xs rounded hover:bg-[#e3e8ef] transition-colors duration-200"
                  >
                    <FileText className="w-3 h-3 mr-1" />
                    Slides
                  </a>
                </div>
              </div>
              <div className="ml-4">
                <img 
                  src="/files/talks/imgs/imperial.png" 
                  alt="Imperial College London" 
                  className="w-16 h-16 object-contain rounded-lg shadow-sm"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* 2024 Talks */}
      <motion.div 
        className="mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.6 }}
      >
        <h2 className="font-display text-3xl font-semibold text-stone-800 mb-6 flex items-center">
          <span className="w-8 h-px bg-stone-500 mr-4"></span>
          2024
        </h2>
        <div className="space-y-6">
          <motion.div 
            className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-stone-200/70 hover:shadow-xl transition-all duration-300 group"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.8 }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-3">
                  <span className="text-sm font-semibold text-[#5b6678] bg-[#eef1f5] px-3 py-1 rounded-full">
                    28/11 - 17:00 UTC+1
                  </span>
                </div>
                <h3 className="font-display text-3xl font-semibold text-stone-800 mb-2 group-hover:text-[#5b6678] transition-colors duration-300">
                  {t('talks.talks2024.llmMarkovImperial.title')}
                </h3>
                <p className="text-stone-700 mb-3">
                  <span className="font-medium">{t('talks.venue')}:</span> {t('talks.talks2024.llmMarkovImperial.venue')}
                </p>
                <p className="text-stone-600 text-sm mb-3">
                  {t('talks.talks2024.llmMarkovImperial.description')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <a href="https://www.youtube.com/watch?v=sgSDOI8QWFY" target="_blank" rel="noopener noreferrer" 
                     className="inline-flex items-center px-2 py-1 bg-[#eef1f5] text-[#5b6678] text-xs rounded hover:bg-[#e3e8ef] transition-colors duration-200">
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Youtube
                  </a>
                </div>
              </div>
              <div className="ml-4">
                <img 
                  src="/files/talks/imgs/alanturing.jpg" 
                  alt="Machine Learning and Dynamical Systems Seminar" 
                  className="w-16 h-16 object-contain rounded-lg shadow-sm"
                />
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-stone-200/70 hover:shadow-xl transition-all duration-300 group"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 2.0 }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-3">
                  <span className="text-sm font-semibold text-[#5b6678] bg-[#eef1f5] px-3 py-1 rounded-full">
                    28/11 - 11:00 UTC+1
                  </span>
                </div>
                <h3 className="font-display text-3xl font-semibold text-stone-800 mb-2 group-hover:text-[#5b6678] transition-colors duration-300">
                  {t('talks.talks2024.llmMarkovTII.title')}
                </h3>
                <p className="text-stone-700 mb-3">
                  <span className="font-medium">{t('talks.venue')}:</span> {t('talks.talks2024.llmMarkovTII.venue')}
                </p>
                <p className="text-stone-600 text-sm">
                  {t('talks.talks2024.llmMarkovTII.description')}
                </p>
              </div>
              <div className="ml-4">
                <img 
                  src="/files/talks/imgs/tii.jpeg" 
                  alt="Technology Innovation Institute" 
                  className="w-16 h-16 object-contain rounded-lg shadow-sm"
                />
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-stone-200/70 hover:shadow-xl transition-all duration-300 group"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 2.2 }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-3">
                  <span className="text-sm font-semibold text-[#5b6678] bg-[#eef1f5] px-3 py-1 rounded-full">
                    14/11 - 18:00 UTC+1
                  </span>
                </div>
                <h3 className="font-display text-3xl font-semibold text-stone-800 mb-2 group-hover:text-[#5b6678] transition-colors duration-300">
                  {t('talks.talks2024.llmMarkovSSI.title')}
                </h3>
                <p className="text-stone-700 mb-3">
                  <span className="font-medium">{t('talks.venue')}:</span> {t('talks.talks2024.llmMarkovSSI.venue')}
                </p>
                <p className="text-stone-600 text-sm mb-3">
                  {t('talks.talks2024.llmMarkovSSI.description')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="/assets/LLM_as_Markov_Chains_SSI.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-2 py-1 bg-[#eef1f5] text-[#5b6678] text-xs rounded hover:bg-[#e3e8ef] transition-colors duration-200"
                  >
                    <FileText className="w-3 h-3 mr-1" />
                    Slides
                  </a>
                  <a href="https://www.youtube.com/watch?v=3VZf0Qu2Egg" target="_blank" rel="noopener noreferrer" 
                     className="inline-flex items-center px-2 py-1 bg-[#eef1f5] text-[#5b6678] text-xs rounded hover:bg-[#e3e8ef] transition-colors duration-200">
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Youtube
                  </a>
                </div>
              </div>
              <div className="ml-4">
                <img 
                  src="/files/talks/imgs/ssi.png" 
                  alt="SSI Club" 
                  className="w-16 h-16 object-contain rounded-lg shadow-sm"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Contact Section */}
      <motion.div 
        className="mt-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 2.4 }}
      >
        <div className="rounded-[1.75rem] border border-stone-200/80 bg-[linear-gradient(135deg,rgba(247,249,252,0.95),rgba(238,241,245,0.92))] p-8 shadow-[0_20px_60px_rgba(31,41,55,0.08)]">
          <h2 className="font-display text-4xl font-semibold text-stone-800 mb-6 flex items-center">
            <span className="w-8 h-px bg-[#5b6678] mr-4"></span>
            {t('talks.inviteMe')}
          </h2>
          <p className="text-stone-700 mb-4">
            {t('talks.inviteDesc')}
          </p>
          <a href="mailto:oussama.zekri@ens-paris-saclay.fr" 
             className="inline-flex items-center px-6 py-3 bg-[#5b6678] text-white font-medium rounded-lg hover:bg-[#4b5565] transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
            <Mail className="w-5 h-5 mr-2" />
            {t('talks.contactMe')}
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Talks; 
